import { useParams, useSearchParams } from "react-router";
import { DocumentPreview } from "~/molecules/DocumentPreview";
import { useDocument } from "~/api/v2/useDocument";
import { useLegacyVersionTarget } from "~/api/swr/useLegacyVersionTarget";
import { CenterSpinner } from "~/atoms/spinner";
import { useTranslation } from "react-i18next";
import NoResult from "~/molecules/NoResult";
import { useState, useEffect } from "react";
import { ReleaseSelect } from "~/molecules/ReleaseSelect";
import { DocumentBreadcrumbs } from "~/molecules/DocumentBreadcrumbs";
import { useUserContext } from "~/hooks/useUserContext";
import { PlatformRole } from "~/config/types";
import { ArchivedWarning } from "~/molecules/ArchivedWarning";
import { DocumentConfetti } from "~/molecules/DocumentConfetti";
import { Link } from "react-router";
import { Button, buttonVariants } from "~/atoms/button";
import { History, Ban, Copy, MoreVertical, Archive } from "lucide-react";
import { FetchError } from "~/api/fetcher";
import { toast } from "react-toastify";
import { DocumentPanelTabs } from "~/molecules/DocumentPanelTabs";
import { EditableDocumentTitle } from "~/molecules/EditableDocumentTitle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "~/atoms/dropdown-menu";
import { archiveDocument } from "~/api/v2/requests";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "~/atoms/alert-dialog";
import {
  canArchiveDocument,
  canEditDocumentContent,
  isDocumentOrArtifactArchived,
} from "~/helpers/documentPermissions";
import { SharedFromTag } from "~/molecules/SharedFromTag";
import { getPublishedDocumentUrl } from "~/helpers/publishedDocumentLinks";
import { OpenInWordDialog } from "~/molecules/OpenInWordDialog";
import { consumeWordLaunchIntent } from "./publishedDocumentResolver";

const ArchiveDocumentDropdownMenuItem = ({
  documentId,
  onArchive,
}: {
  documentId: number;
  onArchive: () => void;
}) => {
  const { t } = useTranslation();

  const handleArchiveDocument = async () => {
    try {
      await archiveDocument(documentId);
      toast.success(t(($) => $.toasts.success.projectArchived));
      void onArchive();
    } catch {
      toast.error(t(($) => $.toasts.error.projectNotArchived));
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <DropdownMenuItem
          className="text-destructive"
          onSelect={(e) => e.preventDefault()}
        >
          <Archive className="size-4" />
          {t(($) => $.commons.buttons.archiveProject)}
        </DropdownMenuItem>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(($) => $.modal.archiveProject.title)}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(($) => $.modal.archiveProject.confirmDescription)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            {t(($) => $.commons.buttons.cancel)}
          </AlertDialogCancel>
          <AlertDialogAction
            className={buttonVariants({ variant: "destructive" })}
            onClick={handleArchiveDocument}
          >
            {t(($) => $.commons.buttons.archiveProject)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const DocumentPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { user } = useUserContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const releaseIdFromUrl = searchParams.get("releaseId");
  const releaseId = releaseIdFromUrl ? parseInt(releaseIdFromUrl) : undefined;
  const shouldOpenInWord = searchParams.get("open") === "word";

  // Pre-migration links pin a release via ?versionId; releaseId takes precedence
  const legacyVersionId = releaseIdFromUrl
    ? null
    : searchParams.get("versionId");
  const { data: legacyTarget, error: legacyVersionError } =
    useLegacyVersionTarget(legacyVersionId);

  const [showConfetti, setShowConfetti] = useState(false);
  const [isWordLaunchDialogOpen, setIsWordLaunchDialogOpen] =
    useState(shouldOpenInWord);

  const { data: doc, isLoading, error, mutate } = useDocument(id);

  const selectedRelease = releaseId
    ? doc?.releases.find((release) => release.id === releaseId)
    : doc?.releases[0];

  useEffect(() => {
    if (!legacyVersionId || (!legacyTarget && !legacyVersionError)) return;

    const next = new URLSearchParams(searchParams);
    next.delete("versionId");
    // A target on another document means the id was reused; treat as unknown
    if (legacyTarget?.documentId === Number(id)) {
      next.set("releaseId", String(legacyTarget.releaseId));
    }
    setSearchParams(next, { replace: true });
  }, [
    legacyVersionId,
    legacyTarget,
    legacyVersionError,
    id,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!doc || !shouldOpenInWord) return;

    setSearchParams(consumeWordLaunchIntent(searchParams), { replace: true });
  }, [doc, searchParams, setSearchParams, shouldOpenInWord]);

  useEffect(() => {
    if (!doc || !shouldOpenInWord) return;

    setSearchParams(consumeWordLaunchIntent(searchParams), { replace: true });
  }, [doc, searchParams, setSearchParams, shouldOpenInWord]);

  useEffect(() => {
    if (doc?.name) {
      document.title = `${doc.name} | North House`;
    }
  }, [doc?.name]);

  useEffect(() => {
    if (doc?.id == null || selectedRelease?.id == null) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [doc?.id, selectedRelease?.id]);

  if (isLoading || legacyVersionId) {
    return <CenterSpinner withText />;
  }

  if (error && error instanceof FetchError && error.status === 403) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        <Ban className="text-muted-foreground size-10" />
        <p className="text-muted-foreground text-center font-medium">
          {t(($) => $.commons.document.youDontHaveAccess.title)}
        </p>
        <p className="text-muted-foreground text-center text-sm">
          {t(($) => $.commons.document.youDontHaveAccess.subtitle)}
        </p>
      </div>
    );
  }

  if (!doc || !selectedRelease) {
    return <NoResult message={t(($) => $.commons.noItems)} />;
  }

  const showArchivedWarning = isDocumentOrArtifactArchived(
    selectedRelease,
    doc.archivedAt,
  );
  const canEditDocument = canEditDocumentContent(
    selectedRelease,
    doc.archivedAt,
  );

  const triggerConfetti = () => {
    setShowConfetti(true);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(
        getPublishedDocumentUrl(window.location.origin, doc.projectId),
      );
      toast.success(t(($) => $.toasts.success.linkCopied));
    } catch {
      toast.error(t(($) => $.toasts.error.copyFailed));
    }
  };

  return (
    <div className="container grid grid-rows-[min-content_1fr] py-6">
      <DocumentConfetti
        show={showConfetti}
        onComplete={() => setShowConfetti(false)}
      />
      <OpenInWordDialog
        open={isWordLaunchDialogOpen}
        onOpenChange={setIsWordLaunchDialogOpen}
        release={selectedRelease}
      />

      <div className="flex justify-between">
        <DocumentBreadcrumbs
          documentId={doc.id}
          releaseId={selectedRelease.id}
          name={doc.name}
        />
        {showArchivedWarning && (
          <ArchivedWarning id={doc.id} archivedAt={doc.archivedAt} />
        )}
      </div>

      <div className="flex flex-wrap justify-between pt-2">
        <div className="flex flex-1 items-center gap-3">
          {canEditDocument ? (
            <EditableDocumentTitle document={doc} onUpdate={mutate} />
          ) : (
            <h2 className="text-2xl font-semibold text-wrap wrap-break-word">
              {doc.name}
            </h2>
          )}
          {doc.sharedFrom && <SharedFromTag sharedFrom={doc.sharedFrom} />}
        </div>

        <div className="flex items-center gap-2">
          {user.role !== PlatformRole.reader && (
            <ReleaseSelect
              selectedReleaseId={selectedRelease.id}
              releases={doc.releases}
              documentId={doc.id}
              onReleaseChange={mutate}
            />
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleCopyLink}>
                <Copy className="size-4" />
                {t(($) => $.commons.workflow.copyLinkToPublishedDocument)}
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={`/documents/${doc.id}/audit-trail`}>
                  <History className="size-4" />
                  {t(($) => $.commons.documentHistory.header)}
                </Link>
              </DropdownMenuItem>
              {canArchiveDocument(selectedRelease, doc.archivedAt) && (
                <ArchiveDocumentDropdownMenuItem
                  documentId={doc.id}
                  onArchive={mutate}
                />
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 pt-2">
        <div className="col-span-3 xl:col-span-2">
          <DocumentPreview
            key={selectedRelease.artifact.fileUpdatedAt}
            onUpdate={mutate}
            release={selectedRelease}
          />
        </div>
        <div className="col-span-3 xl:col-span-1">
          <DocumentPanelTabs
            user={user}
            selectedRelease={selectedRelease}
            document={doc}
            onChange={mutate}
            onConfetti={triggerConfetti}
          />
        </div>
      </div>
    </div>
  );
};
